<template>
	<div class="token-packages">
		<h2 class="token-packages__title">Choose Token Package</h2>
		<p class="token-packages__subtitle">Select the package that best fits your needs</p>

		<div class="token-packages__list">
			<button
				v-for="(pkg, idx) in packages"
				:key="pkg.tokens"
				class="token-packages__package"
				:class="{
					'token-packages__package--selected': idx === selectedIndex,
					'pkg--popular': pkg.popular,
				}"
				@click="select(idx)">
				<div class="token-packages__package-left">
					<span class="token-packages__package-dot-container">
						<span class="token-packages__package-dot" />
					</span>
					<div class="token-packages__package-texts">
						<template v-if="pkg.tokens > 0">
							<div class="token-packages__package-name">{{ pkg.tokens }} tokens</div>
							<div class="token-packages__package-hint">{{ pkg.hint }}</div>
						</template>
						<template v-else-if="tokensNeeded > 5">
							<div class="token-packages__package-name">Use owned tokens</div>
							<div class="token-packages__package-hint">{{ pkg.hint }}</div>
						</template>
						<template v-else>
							<div class="token-packages__package-name">Special Alpha Offer</div>
							<div class="token-packages__package-hint">{{ pkg.hint }}</div>
						</template>
					</div>
				</div>

				<div
					v-if="pkg.tokens > 0"
					class="token-packages__package-right">
					<span
						v-if="pkg.popular && false"
						class="token-packages__package-badge"
						>Popular</span
					>
					<span class="token-packages__package-price">{{ pkg.price }} €</span>
				</div>
				<div
					v-else-if="tokensNeeded > 5"
					class="token-packages__package-right">
					<span class="token-packages__package-price"
						>{{ tokensNeeded }} <span class="token-packages__package-unit">tok</span></span
					>
				</div>
				<div
					v-else
					class="token-packages__package-right">
					<span class="token-packages__package-price">Free</span>
				</div>
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
const props = defineProps<{
	tokensShort: number
	tokensNeeded: number
}>()
const emit = defineEmits<{ (e: 'update:selected', pkg: Package): void }>()

export type Package = {
	tokens: number
	price: number
	price_id?: string
	hint: string
	popular?: boolean
	quantity?: number
}

const packages = computed<Package[]>(() => {
	const options: Package[] = [
		{
			tokens: 50,
			price: 150,
			price_id: 'price_1SDlqpGP2U7AnERXJhKbM04i',
			hint: 'Best for small projects',
			quantity: 1,
		},
		{
			tokens: 200,
			price: 300,
			price_id: 'price_1SDlqsGP2U7AnERXFAYI2sAV',
			hint: 'Most chosen package',
			popular: true,
			quantity: 1,
		},
		{
			tokens: 500,
			price: 500,
			price_id: 'price_1SDlqvGP2U7AnERXpcua6nPI',
			hint: 'Best value for teams',
			quantity: 1,
		},
	]
	if (props.tokensShort > 0 && props.tokensNeeded > 5) {
		options.unshift({
			tokens: props.tokensShort,
			price: props.tokensShort * 5,
			hint: 'Buy missing tokens',
			price_id: 'price_1SDlvNGP2U7AnERX0MuDyPAf',
			quantity: props.tokensShort,
		})
	} else if (props.tokensNeeded > 5) {
		options.unshift({
			tokens: 0,
			price: 0,
			hint: 'Use what you have',
		})
	} else {
		options.unshift({
			tokens: 0,
			price: 0,
			hint: 'For apps with 5 or fewer modules',
		})
	}
	return options
})

const selectedIndex = ref(0)

function select(idx: number) {
	selectedIndex.value = idx
	emit('update:selected', packages.value[idx]!)
}

watch(
	packages,
	() => {
		emit('update:selected', packages.value[selectedIndex.value]!)
	},
	{ immediate: true },
)
</script>

<style scoped lang="scss">
.token-packages {
	--card-bg: var(--p-surface-900);
	--border: var(--p-surface-800);
	--muted: var(--p-surface-400);
	background: var(--card-bg);
	border: 1px solid var(--border);
	border-radius: 12px;
	padding: 16px;

	.token-packages__title {
		font-size: 22px;
		font-weight: 700;
	}

	.token-packages__subtitle {
		color: var(--muted);
		margin: 6px 0 16px 0;
	}

	.token-packages__list {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.token-packages__package {
		width: 100%;
		background: transparent;
		border: 1px solid var(--border);
		border-radius: 12px;
		padding: 16px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		cursor: pointer;
		text-align: left;

		.token-packages__package-left {
			display: flex;
			align-items: center;
			gap: 12px;
		}

		.token-packages__package-dot-container {
			width: 16px;
			height: 16px;
			border-radius: 100%;
			border: 1px solid var(--border);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			background: var(--card-bg);

			.token-packages__package-dot {
				width: 8px;
				height: 8px;
				border-radius: 100%;
				background: transparent;
			}
		}

		.token-packages__package-texts {
			display: flex;
			flex-direction: column;
			gap: 2px;

			.token-packages__package-name {
				font-weight: 700;
			}

			.token-packages__package-hint {
				color: var(--muted);
			}
		}

		.token-packages__package-right {
			display: flex;
			align-items: center;
			gap: 10px;

			.token-packages__package-badge {
				background: rgba(238, 255, 1, 0.18);
				border: 1px solid rgba(238, 255, 1, 0.35);
				color: var(--p-yellow-400);
				padding: 4px 8px;
				border-radius: 8px;
				font-size: 12px;
				font-weight: 600;
			}

			.token-packages__package-price {
				font-weight: 800;
				font-size: 20px;
				margin-right: 10px;

				&:has(.token-packages__package-unit) {
					margin-right: 0;
				}

				.token-packages__package-unit {
					font-size: 14px;
				}
			}
		}

		&--selected {
			border-color: rgba(238, 255, 1, 0.6);
			background: rgba(238, 255, 1, 0.05);
			box-shadow: 0 0 0 1px rgba(238, 255, 1, 0.4) inset;

			.token-packages__package-dot-container {
				.token-packages__package-dot {
					background: var(--p-yellow-500);
				}
			}
		}
	}
}
</style>
